import { inject, Injectable, resource, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { firstValueFrom } from "rxjs";
import { environment } from "src/environments/environment";
import { AuthService } from "../../../subscription/services/auth/auth.service";
import { ClassJaxService } from "../../services/classjax.service";
import { JaxReference } from "./jax.messages";

export interface PromptTemplate {
  id: string;
  title: string;
  message: string;
  enabled: boolean;
  sortOrder: number;
  attachments: JaxReference[];
}

export interface PromptCategory {
  id: string;
  label: string;
  enabled: boolean;
  sortOrder: number;
  prompts: PromptTemplate[];
}

export interface UserPromptsBlob {
  categories: PromptCategory[];
}

interface UserPromptsResponse {
  prompts: UserPromptsBlob;
  generated_at: string | null;
  updated_at: string | null;
}

const EMPTY_BLOB: UserPromptsBlob = { categories: [] };

/** Thrown by save() when another window has saved since this client's last read. */
export class ConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ConflictError";
  }
}

@Injectable({ providedIn: "root" })
export class UserPromptsService {
  private api = inject(ClassJaxService);
  private auth = inject(AuthService);

  /** Bumped to force the prompts resource to reload after a save/regenerate. */
  private revision = signal(0);

  /**
   * Tracks the authenticated user. The resource keys on this so it stays
   * idle until a user is actually signed in, and re-fires automatically
   * when the user changes (e.g. on first sign-in after app boot).
   */
  private currentUser = toSignal(this.auth.$user);

  /**
   * The user's prompt blob. Lazily fetched the first time something reads
   * `prompts.value()`. If the user has never been seeded, the loader
   * transparently calls the LLM seed endpoint and returns the result.
   */
  prompts = resource({
    request: () => {
      const user = this.currentUser();
      if (!user) return undefined;
      return { userId: user.id, rev: this.revision() };
    },
    loader: async () => {
      const existing = await firstValueFrom(
        this.api.get<{ success: boolean; data: UserPromptsResponse }>(
          "/my/user-prompts",
        ),
      );
      if (existing.data?.generated_at) return existing.data;
      return await this.seedViaJax(false);
    },
  });

  async save(blob: UserPromptsBlob): Promise<void> {
    const expectedUpdatedAt = this.prompts.value()?.updated_at ?? null;
    try {
      await firstValueFrom(
        this.api.put<{ success: boolean; data: UserPromptsResponse }>(
          "/my/user-prompts",
          { prompts: blob, expected_updated_at: expectedUpdatedAt },
        ),
      );
    } catch (err: any) {
      if (err?.status === 409) {
        throw new ConflictError(
          "Your prompts were updated in another window. Reload to see the latest version.",
        );
      }
      throw err;
    }
    this.revision.update((r) => r + 1);
  }

  /** Force the resource to refetch the canonical state. */
  reload(): void {
    this.revision.update((r) => r + 1);
  }

  async regenerate(): Promise<void> {
    await this.seedViaJax(true);
    this.revision.update((r) => r + 1);
  }

  private async seedViaJax(force: boolean): Promise<UserPromptsResponse> {
    const userToken = await this.auth.getUserToken();
    if (!userToken) throw new Error("Not authenticated");

    const response = await fetch(
      `${environment.CLASSJAX_AI_URL}/user-prompts/generate`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify({ force }),
      },
    );

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(`Failed to generate prompts: ${response.status} ${text}`);
    }

    const body = await response.json() as {
      success: boolean;
      data: UserPromptsResponse;
    };
    return body.data;
  }
}

export { EMPTY_BLOB };
